import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect } from 'react';
import { createSelector } from 'reselect';
import { withRouter } from 'react-router-dom';
import { push } from 'connected-react18-router';
import { List, Map } from 'immutable';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';

import { User } from '../components/User';
import connectOptions, { mergeProps } from '../utils/connectOptions';
import {
  loadUsers,
  loadSuggestionUsers,
  loadUserDetails,
  setUserListFilters,
  changeMode,
  loadRoles,
  loadDashboardUserTitles,
  clearSelectedUser,
  updateAttempts,
  loadReports,
  deleteGoogle2FAAuth,
  loadTitles,
} from '../actions/userManage';
import config from '../config';
import { showError } from '../utils/MessagePopup';
import downloadFile from '../utils/downloadFile';
import { usePopupContext } from '../contexts/PopupContext';

/**
 * User container component.
 *
 * @param props {Object}.
 * @param props.users {Immutable.List} User list.
 * @param props.roles {Immutable.List} Role list.
 * @param props.filters {Immutable.Map} User list filters.
 * @param props.suggestionUsers {Immutable.List} All users (including inactive) for module/supervisor suggestions.
 * @param props.userLoading {Boolean} If set to true will show user loading spinner icon.
 * @param props.tableCells {Immutable.List} Table cell configuration.
 * @param props.errors {Immutable.List} Error list.
 */
const UserContainer = props => {
  const {
    match: {
      params: { id },
    },
    user,
    filters,
    loadUsers,
    loadSuggestionUsers,
    loadUserDetails,
    setUserListFilters,
    loadRoles,
    loadTitles,
    loadDashboardUserTitles,
    loadReports,
    changeMode,
    clearSelectedUser,
    updateAttempts,
    push,
    deleteGoogle2FAAuth,
  } = props;

  const { openPopup, closePopup } = usePopupContext();

  const userHasAccess = user ? user.getIn(['roles', 0, 'slug'], null) === config.ADMINISTRATOR : false;

  const showWarningPopup = useCallback(() => {
    showError(openPopup, config.permisionError, () => {
      push('/');
      closePopup();
    });
  }, [openPopup, closePopup, push]);

  useEffect(() => {
    loadReports();

    if (userHasAccess) {
      const userId = parseInt(id, 10);

      loadUsers(filters);
      loadSuggestionUsers();
      if (userId > 0) {
        loadUserDetails(userId);
      }
      loadRoles();
      loadTitles();
      loadDashboardUserTitles();
    } else {
      showWarningPopup();
    }
  }, []);

  const handleFiltersChange = useCallback(
    changedFilters => {
      const nextFilters = filters.merge(changedFilters);

      setUserListFilters(nextFilters);
      loadUsers(nextFilters);
    },
    [filters, setUserListFilters, loadUsers],
  );

  const handleExportUserList = useCallback(() => {
    const url = `${config.API_BASE_URL}/api/v1/users/export`;

    downloadFile({ url, params: { is_hide_inactive: filters.get('includeInactive') ? '0' : '1' } });
  }, [filters]);

  const handleUserSave = useCallback(() => {
    loadUsers(filters);
    loadSuggestionUsers();
    loadTitles();
  }, [filters, loadUsers, loadSuggestionUsers, loadTitles]);

  const handleEdit = useCallback(
    userId => {
      clearSelectedUser();
      changeMode('edit');
      updateAttempts(false);
      if (userId) {
        loadUserDetails(userId);
      }
      openPopup('UserDetailsPopup', { userId, onSave: handleUserSave });
    },
    [clearSelectedUser, changeMode, updateAttempts, loadUserDetails, openPopup, handleUserSave],
  );

  const handleAddNew = useCallback(() => handleEdit(), [handleEdit]);

  const handleRemove2FaClick = useCallback(
    userId => {
      openPopup('ConfirmPopup', {
        message: 'Are you sure to confirm remove 2FA?',
        onOk: () =>
          deleteGoogle2FAAuth({
            userId,
            afterSuccess: () => {
              closePopup();
            },
          }),
        onCancel: () => {
          closePopup();
        },
        yes: 'Yes',
        no: 'No',
      });
    },
    [openPopup, deleteGoogle2FAAuth, closePopup],
  );

  const content = userHasAccess ? (
    <User
      {...props}
      onAddClick={handleAddNew}
      onEdit={handleEdit}
      onExportUserList={handleExportUserList}
      onFiltersChange={handleFiltersChange}
      onRemove2FaClick={handleRemove2FaClick}
      onSortChange={handleFiltersChange}
    />
  ) : null;

  return (
    <div className="full-height">
      <Helmet title="User Page" />
      {content}
    </div>
  );
};

UserContainer.propTypes = {
  changeMode: PropTypes.func.isRequired,
  clearSelectedUser: PropTypes.func.isRequired,
  dashboardUserTitles: PropTypes.instanceOf(List).isRequired,
  deleteGoogle2FAAuth: PropTypes.func.isRequired,
  errors: PropTypes.instanceOf(List).isRequired,
  filters: PropTypes.instanceOf(Map).isRequired,
  loadDashboardUserTitles: PropTypes.func.isRequired,
  loadReports: PropTypes.func.isRequired,
  loadRoles: PropTypes.func.isRequired,
  loadSuggestionUsers: PropTypes.func.isRequired,
  loadTitles: PropTypes.func.isRequired,
  loadUserDetails: PropTypes.func.isRequired,
  loadUsers: PropTypes.func.isRequired,
  roles: PropTypes.instanceOf(List).isRequired,
  setUserListFilters: PropTypes.func.isRequired,
  suggestionUsers: PropTypes.instanceOf(List).isRequired,
  tableCells: PropTypes.instanceOf(List).isRequired,
  updateAttempts: PropTypes.func.isRequired,
  userLoading: PropTypes.bool.isRequired,
  users: PropTypes.instanceOf(List).isRequired,
};

const selectUsers = createSelector(
  state => state.user,
  state => state.get('users'),
);

function mapStateToProps(state) {
  return {
    users: selectUsers(state),
    suggestionUsers: state.user.get('suggestionUsers'),
    filters: state.user.get('filters'),
    roles: state.user.get('roles'),
    dashboardUserTitles: state.user.get('dashboardUserTitles'),
    userLoading: state.user.get('userLoading'),
    tableCells: config.tables.getIn(['userList', 'columns']),
    errors: state.user.get('errors'),
    user: state.auth.get('user', null),
  };
}

const mapDispatchToProps = {
  loadUsers,
  loadSuggestionUsers,
  loadUserDetails,
  setUserListFilters,
  changeMode,
  loadRoles,
  loadDashboardUserTitles,
  loadTitles,
  clearSelectedUser,
  updateAttempts,
  loadReports,
  push,
  deleteGoogle2FAAuth,
};

export { UserContainer };
export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(withRouter(memo(UserContainer)));
